import { useEffect, useRef, useState } from 'react';
import { NavLink } from '@/components/NavLink';
import { ArrowLeft } from 'lucide-react';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../ConsolePanel';

/**
 * The 404, still an A/B split wiped by the cursor — the joke is the product.
 *
 * Two things changed. It is no longer `absolute inset-0 h-screen`: the console
 * shell gives every screen a panel with its own rounded clip and the app's only
 * scroll container, and a full-viewport overlay escaped both. And the split is
 * measured against this element rather than `window.innerWidth`, so the seam
 * still lands under the pointer now that the screen is inset by a sidebar.
 *
 * The slate/purple/cyan gradients are gone. A and B are `sel` and `accent`,
 * which is what those tokens mean everywhere else in the console.
 */
export const FourOhFour = () => {
  const [mouseX, setMouseX] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (ev: MouseEvent) => {
      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width === 0) return;
      const x = ((ev.clientX - bounds.left) / bounds.width) * 100;
      setMouseX(Math.max(0, Math.min(100, x)));
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      ref={containerRef}
      className="border-line bg-panel relative min-h-[440px] overflow-hidden rounded-panel border"
    >
      {/*
        One real route home, for the keyboard.
        Both halves are always in the DOM and each is clipped by cursor position, so
        tabbing used to land focus on a "Return"/"Go Home" link clipped to zero width
        — focus simply vanished — and offered the same destination twice. The two
        halves are decoration now (`aria-hidden`, and their links are not focusable),
        and this is the single tab stop: invisible until focused, then fully drawn
        where it cannot be clipped.
      */}
      <NavLink
        to="/"
        className={cn(
          'bg-cta hover:bg-ctahover absolute top-4 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-2',
          'rounded-lg px-4 py-2 text-body font-medium text-white transition-opacity duration-150',
          'pointer-events-none opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100',
          FOCUS_RING,
        )}
      >
        <ArrowLeft className="size-4" strokeWidth={1.5} />
        Go to the dashboard
      </NavLink>

      {/* Variant A */}
      <div
        aria-hidden="true"
        className="bg-panel absolute inset-0 flex flex-col items-center justify-center px-4 py-12"
        style={{ clipPath: `polygon(0 0, ${mouseX}% 0, ${mouseX}% 100%, 0 100%)` }}
      >
        <div className="max-w-2xl text-center">
          <div className="text-sel mb-6 font-mono text-[40px] font-medium leading-[1.05] tracking-[-0.02em]">
            May the 404 be with you
          </div>
          <h2 className="text-foreground mb-2 text-[20px] font-medium">This is not the page...</h2>
          <p className="supporting mb-8">... you are looking for.</p>
          <NavLink
            to="/"
            tabIndex={-1}
            className="bg-cta hover:bg-ctahover inline-flex items-center gap-2 rounded-lg px-4 py-2 text-body font-medium text-white transition-colors duration-150"
          >
            <ArrowLeft className="size-4" strokeWidth={1.5} />
            Return
          </NavLink>
        </div>
      </div>

      {/* Variant B */}
      <div
        aria-hidden="true"
        className="bg-panel2 absolute inset-0 flex flex-col items-center justify-center px-4 py-12"
        style={{ clipPath: `polygon(${mouseX}% 0, 100% 0, 100% 100%, ${mouseX}% 100%)` }}
      >
        <div className="max-w-md text-center">
          <div className="text-accent mb-6 font-mono text-[40px] font-medium leading-[1.05] tracking-[-0.02em]">
            404
          </div>
          <h2 className="text-foreground mb-2 text-[20px] font-medium">Page Not Found</h2>
          <p className="supporting mb-8">The resource you requested is unavailable.</p>
          <NavLink
            to="/"
            tabIndex={-1}
            className="border-input text-foreground hover:bg-hover inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-body font-medium transition-colors duration-150"
          >
            <ArrowLeft className="size-4" strokeWidth={1.5} />
            Go Home
          </NavLink>
        </div>
      </div>

      {/* The seam the cursor drags. */}
      <div className="bg-line absolute bottom-0 top-0 w-px" style={{ left: `${mouseX}%` }} />
    </div>
  );
};
